import { BaseFeature } from '../../core/base-feature.js';

/**
 * TaskBoardFeature - Frontend feature for managing tasks
 * Handles: viewing tasks, creating tasks, approving/rejecting, task submissions
 */
export class TaskBoardFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        // State
        this.tasks = [];
        this.taskCounts = {};

        // Event callbacks for React components
        this.taskUpdateCallbacks = new Set();
        this.updateCallbacks = new Set();

        // Pending promises for request/response
        this._pendingRequests = new Map();
    }

    static getMetadata() {
        return {
            id: 'task-board',
            name: 'Task Board',
            version: 'v1.0',
            capabilities: [
                'view_tasks',
                'create_tasks',
                'approve_reject_tasks',
                'submit_tasks'
            ],
            dependencies: ['economy']
        };
    }

    async initialize() {
        console.log('[TaskBoard] Initializing...');
        console.log('[TaskBoard] Initialized');
    }

    async handleMessage(message) {
        const { action, cmd, payload, messageId, fromId } = message;
        const command = cmd || action;

        switch (command) {
            // Real-time updates
            case 'task_update':
                return this.handleTaskUpdate(payload);

            case 'initial_state':
                return this.handleInitialState(payload);

            // Responses to commands
            case 'get_tasks_response':
                return this.handleResponse('get_tasks', payload);

            case 'get_task_response':
                return this.handleResponse('get_task', payload);

            case 'create_task_response':
                return this.handleResponse('create_task', payload);

            case 'submit_task_response':
                return this.handleResponse('submit_task', payload);

            case 'approve_task_response':
                return this.handleResponse('approve_task', payload);

            case 'reject_task_response':
                return this.handleResponse('reject_task', payload);

            case 'delete_task_response':
                return this.handleResponse('delete_task', payload);

            case 'get_counts_response':
                return this.handleResponse('get_counts', payload);

            default:
                console.warn(`[TaskBoard] Unknown command: ${command}`);
        }
    }

    // =====================================
    // Real-time Event Handlers
    // =====================================

    handleTaskUpdate(payload) {
        console.log('[TaskBoard] Task update received:', payload.action);
        this.notifyTaskUpdateListeners(payload);
    }

    handleInitialState(payload) {
        console.log('[TaskBoard] Received initial state');
        if (payload.tasks) {
            this.tasks = payload.tasks;
        }
        if (payload.counts) {
            this.taskCounts = payload.counts;
        }
        this.notifyUpdateListeners();
    }

    // =====================================
    // Generic Response Handler
    // =====================================

    handleResponse(requestType, payload) {
        const resolve = this._pendingRequests.get(requestType);
        if (resolve) {
            this._pendingRequests.delete(requestType);
            resolve(payload);
        }
    }

    // =====================================
    // Task Methods
    // =====================================

    /**
     * Get tasks with optional filtering
     * @param {Object} options - Query options (status, limit, offset)
     * @returns {Promise<Object>} Tasks list and counts
     */
    async getTasks(options = {}) {
        return new Promise((resolve) => {
            this.sendMessage('get_tasks', options);
            this._pendingRequests.set('get_tasks', resolve);
        });
    }

    /**
     * Get a single task
     * @param {number} taskId - Task ID
     * @returns {Promise<Object>} Task
     */
    async getTask(taskId) {
        return new Promise((resolve) => {
            this.sendMessage('get_task', { taskId });
            this._pendingRequests.set('get_task', resolve);
        });
    }

    /**
     * Create a new task
     * @param {Object} taskData - Task data
     * @returns {Promise<Object>} Result with task
     */
    async createTask(taskData) {
        return new Promise((resolve) => {
            this.sendMessage('create_task', taskData);
            this._pendingRequests.set('create_task', resolve);
        });
    }

    /**
     * Submit a task completion (from desktop)
     * @param {number} taskId - Task ID
     * @param {string} proofData - Optional proof data (base64 image)
     * @returns {Promise<Object>} Result
     */
    async submitTask(taskId, proofData = null) {
        return new Promise((resolve) => {
            this.sendMessage('submit_task', { taskId, proofData });
            this._pendingRequests.set('submit_task', resolve);
        });
    }

    /**
     * Approve a task (credits economy)
     * @param {number} taskId - Task ID
     * @returns {Promise<Object>} Result
     */
    async approveTask(taskId) {
        return new Promise((resolve) => {
            this.sendMessage('approve_task', { taskId });
            this._pendingRequests.set('approve_task', resolve);
        });
    }

    /**
     * Reject a task (applies penalty if configured)
     * @param {number} taskId - Task ID
     * @param {string} reason - Rejection reason
     * @returns {Promise<Object>} Result
     */
    async rejectTask(taskId, reason = null) {
        return new Promise((resolve) => {
            this.sendMessage('reject_task', { taskId, reason });
            this._pendingRequests.set('reject_task', resolve);
        });
    }

    /**
     * Delete a task
     * @param {number} taskId - Task ID
     * @returns {Promise<Object>} Result
     */
    async deleteTask(taskId) {
        return new Promise((resolve) => {
            this.sendMessage('delete_task', { taskId });
            this._pendingRequests.set('delete_task', resolve);
        });
    }

    /**
     * Get task counts by status
     * @returns {Promise<Object>} Counts
     */
    async getCounts() {
        return new Promise((resolve) => {
            this.sendMessage('get_counts', {});
            this._pendingRequests.set('get_counts', resolve);
        });
    }

    // =====================================
    // Event Subscriptions (for React)
    // =====================================

    /**
     * Subscribe to task updates
     * @param {Function} callback - Called with task data
     * @returns {Function} Unsubscribe function
     */
    onTaskUpdate(callback) {
        this.taskUpdateCallbacks.add(callback);
        return () => this.taskUpdateCallbacks.delete(callback);
    }

    /**
     * Subscribe to general updates
     * @param {Function} callback - Called when data changes
     * @returns {Function} Unsubscribe function
     */
    onUpdate(callback) {
        this.updateCallbacks.add(callback);
        return () => this.updateCallbacks.delete(callback);
    }

    notifyTaskUpdateListeners(data) {
        for (const callback of this.taskUpdateCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[TaskBoard] Error in task update callback:', error);
            }
        }
    }

    notifyUpdateListeners() {
        const data = {
            tasks: this.tasks,
            counts: this.taskCounts
        };
        for (const callback of this.updateCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[TaskBoard] Error in update callback:', error);
            }
        }
    }

    // =====================================
    // Getters
    // =====================================

    getTasksList() {
        return this.tasks;
    }

    getTaskCounts() {
        return this.taskCounts;
    }

    // =====================================
    // Cleanup
    // =====================================

    async cleanup() {
        console.log('[TaskBoard] Cleaning up...');
        this.tasks = [];
        this.taskCounts = {};
        this.taskUpdateCallbacks.clear();
        this.updateCallbacks.clear();
        this._pendingRequests.clear();
    }
}
